/**
 * Gastos fijos: what the shop pays every month or every few (the owner, 2026-10-07: «we need a
 * feature where we can register repeating expenses (rent, internet, etc.)»).
 *
 * A fixed expense is a setting, a list like the trucks (`camiones`), not a record of its own: it
 * moves no money until a month of it is recorded as an expense like any other. So adding one needs
 * no migration, and a phone that has not updated refuses the setting and carries on, where an
 * unknown kind of record would be dropped for good.
 *
 * Each month of it is recorded with an id made from the fixed expense and the month
 * (`idDeOcurrencia`): two phones recording the same month without a connection make the same
 * expense twice, and the books, which refuse an id they already have, keep one. Tried again after
 * a void, it takes the next id of the same kind.
 */

import { z } from 'zod';
import { cuentaBancoSchema } from './bancos.js';
import { v5 as uuidv5 } from 'uuid';

/** The expense categories, as `gasto.registrado` takes them. */
export const CATEGORIAS_GASTO_VALORES = [
  'alquiler',
  'energia',
  'agua',
  'telefono_internet',
  'transporte',
  'mantenimiento',
  'impuestos_municipales',
  'empaques',
  'sueldos',
  'otros',
] as const;
export const categoriaGastoSchema = z.enum(CATEGORIAS_GASTO_VALORES);

/** How an expense is paid, as `gasto.registrado` takes it: on credit, it is owed. */
export const formaPagoGastoSchema = z.enum([
  'efectivo_cordobas',
  'efectivo_dolares',
  'transferencia',
  'billetera',
  'credito',
]);

/** A month, `YYYY-MM`. */
export const periodoSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);

export const gastoFijoSchema = z.object({
  id: z.string().uuid(),
  descripcion: z.string().min(1).max(160),
  categoria: categoriaGastoSchema,
  moneda: z.enum(['NIO', 'USD']).default('NIO'),
  /** In the currency above. Null when it changes every time (the light, the water): asked then. */
  monto: z.number().int().positive().nullable().default(null),
  /** How it is usually paid: what the form starts with. */
  formaPago: formaPagoGastoSchema.default('efectivo_cordobas'),
  /** The bank account it is usually paid from, when by transfer (`bancos.ts`): what the form starts with too. */
  cuenta: cuentaBancoSchema.optional(),
  /** Who is paid, when they are in the supplier list. */
  proveedorId: z.string().uuid().nullable().default(null),
  /** The day of the month it falls due; past the end of a short month, that month's last day. */
  dia: z.number().int().min(1).max(31),
  /** Every how many months: 1 every month, 2 every other, 12 once a year. */
  cadaMeses: z.number().int().min(1).max(12).default(1),
  /** The first month it falls due. The months after it are counted from this one. */
  desde: periodoSchema,
  /** Months passed over («Este mes no»): not asked for again. */
  omitidos: z.array(periodoSchema).max(240).default([]),
  /** Paused: nothing is asked for while it is off. */
  activo: z.boolean().default(true),
});
export type GastoFijo = z.infer<typeof gastoFijoSchema>;

/** Fixed, so every phone makes the same id for the same month of the same fixed expense. */
const ESPACIO_GASTOS_FIJOS = '6f1d6a52-6a6b-4c47-9a35-0c1c3b6f2a91';

/**
 * The id of a month of a fixed expense: the same on every phone, so the books keep it once.
 *
 * `vez` is which try: a month whose expense was voided outright did not happen and is asked for
 * again, but the books refuse its id for good, voided or not. So the second try takes the second
 * id, as the same on every phone as the first.
 */
export function idDeOcurrencia(gastoFijoId: string, periodo: string, vez = 1): string {
  return uuidv5(vez === 1 ? `${gastoFijoId}:${periodo}` : `${gastoFijoId}:${periodo}:${vez}`, ESPACIO_GASTOS_FIJOS);
}
